import Image from 'next/image'
import type {Elements, IContentItem} from '@kontent-ai/delivery-sdk'
import {loadImageFromKontentAI} from '@/image-loader'
import type {Block} from '@/services/kontent-ai/types'
import {colorWithFallback} from '@/utilities/hooks/color-with-fallback'
import type {RichTextContentItem} from '@/products/flexible-web-toolkit/blocks/rich-text'
import {RichTextBlock} from '@/products/flexible-web-toolkit/blocks/rich-text'
import type {
	ImageShapeClasses,
	QuoteBorderClasses,
} from '../../components/quote/quote'
import {Quote} from '../../components/quote/quote'

export type QuoteContentItem = IContentItem<{
	quote: Elements.TextElement
	quotee: Elements.TextElement
	richText: Elements.LinkedItemsElement<RichTextContentItem>
	image: Elements.AssetsElement
	imagePlacement: Elements.MultipleChoiceElement
	imageShape: Elements.MultipleChoiceElement
	brandColor: Elements.CustomElement
	/** @deprecated Use brandColor instead. Retained for backward compatibility. */
	borderColor: Elements.MultipleChoiceElement
}>

export const QuoteBlock: Block<QuoteContentItem> = ({
	block,
	page,
	...context
}) => {
	const showImageOnTop = block.elements.imagePlacement[0]?.codename === 'top'

	const {colorId: borderColor, hexCode: borderHexCode} = colorWithFallback({
		blockColor: block.elements.brandColor,
		blockColorLegacy: block.elements.borderColor,
		pageConfig: page.elements.config[0],
		fallbackColorId: 'primary',
	})

	const imageShapeClass = (block.elements.imageShape[0]?.codename ||
		'oval') as keyof typeof ImageShapeClasses

	// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- intentional optional chain for safety
	const richText = block.elements.richText?.[0] ? (
		<RichTextBlock
			block={block.elements.richText[0]}
			page={page}
			{...context}
		/>
	) : null

	return (
		<div data-kontent-element-codename="quote">
			<Quote
				borderColor={
					(borderColor ??
						'primary') as keyof typeof QuoteBorderClasses
				}
				borderHexColor={borderHexCode ?? undefined}
				image={
					block.elements.image[0] ? (
						<Image
							alt={block.elements.image[0].description || ''}
							className="mx-auto h-44 w-auto rounded-full"
							height={block.elements.image[0].height || 164}
							loader={loadImageFromKontentAI}
							src={block.elements.image[0].url}
							width={block.elements.image[0].width || 306}
						/>
					) : null
				}
				imageShape={imageShapeClass}
				quote={block.elements.quote}
				quotee={block.elements.quotee}
				richTextContent={richText}
				showImageOnTop={showImageOnTop}
			/>
		</div>
	)
}
